import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Send, MapPin, Phone, Mail, Clock } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import Seo from "../components/Seo";
import PageBanner from "../components/PageBanner";
import Reveal from "../components/Reveal";
import { CONTACT } from "../data/site";

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY;

const inputCls =
  "w-full bg-[#F4F4F5] border border-transparent rounded-xl px-4 py-3.5 text-[#09090B] placeholder:text-zinc-400 focus:bg-white focus:border-[#6C2BD9] focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/20 transition-colors";
const labelCls = "block text-sm font-medium text-zinc-700 mb-2";

const INFO = [
  { icon: MapPin, label: "Office Address", value: CONTACT.address },
  { icon: Phone, label: "Phone Number", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
  { icon: Mail, label: "Email Address", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
  { icon: Clock, label: "Business Hours", value: CONTACT.hours },
];

export default function Contact() {
  const [agree, setAgree] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agree) {
      setErrorMsg("Please accept the Privacy Policy and Terms & Conditions to proceed.");
      setStatus("error");
      return;
    }
    setStatus("sending");
    setErrorMsg("");

    const form = e.target;
    const data = new FormData(form);
    data.append("access_key", WEB3FORMS_KEY || "");
    data.append("subject", "New Contact Message from CARDED Website");
    data.append("from_name", "CARDED Website");
    data.append("marketing_consent", marketing ? "Yes" : "No");

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: data,
      });
      const json = await res.json();
      if (json.success) {
        setStatus("success");
        form.reset();
        setAgree(false);
        setMarketing(false);
      } else {
        setStatus("error");
        setErrorMsg(json.message || "Submission failed. Please try again.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg("Network error. Please try again or call us directly.");
    }
  };

  return (
    <>
      <Seo
        title="Contact CARDED | Loan Enquiries & Support"
        description="Get in touch with CARDED for loan and credit card enquiries. Call +91-7044086479, email info@carded.in or visit our Kolkata office. Send us a message and we'll respond quickly."
      />
      <PageBanner
        overline="Contact Us"
        title="Let's talk finance."
        subtitle="Have a question or ready to apply? Send us a message and our team will reach out shortly."
      />

      <section className="bg-white pb-24 sm:pb-32" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 sm:px-10 lg:grid-cols-12">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <div className="rounded-3xl border border-[#E4E4E7] bg-white p-6 shadow-xl shadow-[#6C2BD9]/5 sm:p-10">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="s"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center py-16 text-center"
                    data-testid="contact-success-message"
                  >
                    <CheckCircle2 className="h-16 w-16 text-[#6C2BD9]" />
                    <p className="mt-5 text-xl font-medium text-[#09090B]">
                      Thank you. Our representative will contact you shortly.
                    </p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-6 text-sm font-medium text-[#6C2BD9] hover:underline"
                      data-testid="contact-reset-button"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <motion.form key="f" onSubmit={onSubmit} className="space-y-5" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <input type="checkbox" name="botcheck" className="hidden" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label className={labelCls} htmlFor="c-name">Full Name</label>
                        <input id="c-name" name="name" required placeholder="Your full name" className={inputCls} data-testid="contact-name-input" />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="c-mobile">Mobile Number</label>
                        <input id="c-mobile" name="mobile" type="tel" required pattern="[0-9+ ]{10,15}" placeholder="10-digit mobile" className={inputCls} data-testid="contact-mobile-input" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label className={labelCls} htmlFor="c-email">Email</label>
                        <input id="c-email" name="email" type="email" required placeholder="you@example.com" className={inputCls} data-testid="contact-email-input" />
                      </div>
                      <div>
                        <label className={labelCls} htmlFor="c-city">City</label>
                        <input id="c-city" name="city" required placeholder="Your city" className={inputCls} data-testid="contact-city-input" />
                      </div>
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="c-subject">Subject</label>
                      <input id="c-subject" name="subject_line" required placeholder="What is this about?" className={inputCls} data-testid="contact-subject-input" />
                    </div>

                    <div>
                      <label className={labelCls} htmlFor="c-message">Message</label>
                      <textarea id="c-message" name="message" required rows={4} placeholder="Tell us how we can help" className={`${inputCls} resize-none`} data-testid="contact-message-input" />
                    </div>

                    <label className="flex items-start gap-3 text-sm text-zinc-600" data-testid="contact-agree-wrapper">
                      <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5 data-[state=checked]:bg-[#6C2BD9] data-[state=checked]:border-[#6C2BD9]" data-testid="contact-agree-checkbox" />
                      <span>
                        I have read and agree to the{" "}
                        <a href="/privacy-policy" className="font-medium text-[#6C2BD9] hover:underline">Privacy Policy</a> and{" "}
                        <a href="/terms-conditions" className="font-medium text-[#6C2BD9] hover:underline">Terms &amp; Conditions</a>.
                      </span>
                    </label>

                    <label className="flex items-start gap-3 text-xs leading-relaxed text-zinc-500" data-testid="contact-marketing-wrapper">
                      <Checkbox checked={marketing} onCheckedChange={(v) => setMarketing(!!v)} className="mt-0.5 data-[state=checked]:bg-[#6C2BD9] data-[state=checked]:border-[#6C2BD9]" data-testid="contact-marketing-checkbox" />
                      <span>
                        I agree to receive marketing communications, promotional offers, service
                        updates, reminders and notifications from CARDED through SMS, RCS, WhatsApp,
                        phone calls and email. I understand that I can opt out at any time.
                      </span>
                    </label>

                    {status === "error" && (
                      <p className="text-sm text-red-600" data-testid="contact-error-message">{errorMsg}</p>
                    )}

                    <button
                      type="submit"
                      disabled={status === "sending"}
                      className="group inline-flex w-full items-center justify-center gap-2 rounded-full brand-gradient px-8 py-4 font-medium text-white transition-transform hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(108,43,217,0.3)] disabled:opacity-70"
                      data-testid="contact-submit-button"
                    >
                      {status === "sending" ? (
                        <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>
                      ) : (
                        <>Send Message <Send className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>
                      )}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>

          {/* Info + Map */}
          <Reveal direction="left" className="lg:col-span-5">
            <div className="space-y-4">
              {INFO.map((item) => (
                <div key={item.label} className="flex items-start gap-4 rounded-2xl border border-[#E4E4E7] bg-white p-5" data-testid={`contact-info-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F4F4F5] text-[#6C2BD9]">
                    <item.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} className="mt-1 block text-base font-medium text-[#09090B] hover:text-[#6C2BD9]">{item.value}</a>
                    ) : (
                      <p className="mt-1 text-base font-medium text-[#09090B]">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}

              <div className="overflow-hidden rounded-2xl border border-[#E4E4E7]" data-testid="contact-map">
                <iframe
                  title="CARDED office location"
                  src="https://www.google.com/maps?q=Madurdaha,Kolkata,700107&output=embed"
                  className="h-64 w-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
